/* eslint-disable no-console */
interface LogContext {
	[key: string]: unknown;
	error?: unknown;
}

interface Logger {
	error: (message: string, context?: LogContext) => void;
	warn: (message: string, context?: LogContext) => void;
	info: (message: string, context?: LogContext) => void;
	debug: (message: string, context?: LogContext) => void;
}

function extractErrorDetails(error: unknown): {
	message: string;
	stack?: string;
	name?: string;
} {
	if (error instanceof Error) {
		return {
			message: error.message,
			stack: error.stack,
			name: error.name
		};
	}
	return {
		message: String(error)
	};
}

export function useLogger(service?: string): Logger {
	const logError = (message: string, context?: LogContext) => {
		const enrichedContext = { ...context };

		// Extract error details if error is present in context
		if (context?.error) {
			const errorDetails = extractErrorDetails(context.error);
			enrichedContext.error_message = errorDetails.message;
			enrichedContext.error_stack = errorDetails.stack;
			enrichedContext.error_name = errorDetails.name;
			delete enrichedContext.error; // Remove original error object
		}

		if (import.meta.client) {
			// Client-side: use Datadog Logs if available
			if (window.DD_LOGS) {
				window.DD_LOGS.logger.error(message, {
					...enrichedContext,
					...(service && { service })
				});
			} else {
				// Fallback to console if Datadog is not initialized
				console.error(`[${service || 'App'}]`, message, enrichedContext);
			}
		} else {
			// Server-side: use console or Pino logger
			console.error(`[${service || 'App'}]`, message, enrichedContext);
		}
	};

	const logWarn = (message: string, context?: LogContext) => {
		if (import.meta.client) {
			if (window.DD_LOGS) {
				window.DD_LOGS.logger.warn(message, {
					...context,
					...(service && { service })
				});
			} else {
				console.warn(`[${service || 'App'}]`, message, context);
			}
		} else {
			console.warn(`[${service || 'App'}]`, message, context);
		}
	};

	const logInfo = (message: string, context?: LogContext) => {
		if (import.meta.client) {
			if (window.DD_LOGS) {
				window.DD_LOGS.logger.info(message, {
					...context,
					...(service && { service })
				});
			} else {
				console.info(`[${service || 'App'}]`, message, context);
			}
		} else {
			console.info(`[${service || 'App'}]`, message, context);
		}
	};

	const logDebug = (message: string, context?: LogContext) => {
		if (import.meta.client) {
			if (window.DD_LOGS) {
				window.DD_LOGS.logger.debug(message, {
					...context,
					...(service && { service })
				});
			} else {
				console.debug(`[${service || 'App'}]`, message, context);
			}
		} else {
			console.debug(`[${service || 'App'}]`, message, context);
		}
	};

	return {
		error: logError,
		warn: logWarn,
		info: logInfo,
		debug: logDebug
	};
}
